import { trackEvent } from "@analytics";
import { Button, type ButtonSize, type SxProps } from "@biom3/react";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import XIcon from "@mui/icons-material/X";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
} from "@mui/material";
import { useMemo, useState } from "react";

type SocialShareButtonsProps = {
  shareText: string;
  shareUrl: string;
  trackingSection?: string;
  trackingExtras?: Record<string, unknown>;
  size?: ButtonSize;
  sx?: SxProps;
};

export const SocialShareButtons = ({
  shareText,
  shareUrl,
  trackingSection = "RewardsRedemption",
  trackingExtras = {},
  size = "large",
  sx,
}: SocialShareButtonsProps) => {
  const [copied, setCopied] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  // Create X share URL with optimized text
  const xShareUrl = useMemo(() => {
    // Make sure the tweet includes the URL
    const textWithUrl = shareText.includes(shareUrl)
      ? shareText
      : `${shareText} ${shareUrl}`;

    const encodedText = encodeURIComponent(textWithUrl);
    return `https://x.com/intent/tweet?text=${encodedText}`;
  }, [shareText, shareUrl]);

  const handleXShare = () => {
    window.open(xShareUrl, "_blank");
    handleCloseMenu();
    trackEvent({
      component: "Button",
      action: "Pressed",
      extras: {
        control: "ShareOnX",
        section: trackingSection,
        userJourney: "Rewards",
        ...trackingExtras,
      },
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        handleCloseMenu();
      }, 2000);
    });

    trackEvent({
      component: "Button",
      action: "Pressed",
      extras: {
        control: "ShareCopyLink",
        section: trackingSection,
        userJourney: "Rewards",
        ...trackingExtras,
      },
    });
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    trackEvent({
      component: "Button",
      action: "Pressed",
      extras: {
        control: "OpenShareMenu",
        section: trackingSection,
        userJourney: "Rewards",
        ...trackingExtras,
      },
    });
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        size={size}
        variant="secondary"
        onClick={handleOpenMenu}
        sx={{ ...sx }}
      >
        Share
        <Button.Icon icon="SocialShare" />
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <List>
          <ListItemButton onClick={handleXShare}>
            <ListItemIcon sx={{ minWidth: "36px" }}>
              <XIcon />
            </ListItemIcon>
            <ListItemText primary="Share on X" />
          </ListItemButton>

          <ListItemButton onClick={handleCopyLink}>
            <ListItemIcon sx={{ minWidth: "36px" }}>
              {copied ? <CheckIcon /> : <ContentCopyIcon />}
            </ListItemIcon>
            <ListItemText primary={copied ? "Copied!" : "Copy Link"} />
          </ListItemButton>
        </List>
      </Menu>
    </>
  );
};
